<template>

    <Card title="Surplus Reports">

        <template #header-right>
            <div class="d-flex" style="gap:10px;">
                <ExportDropdown class="m-0" :disabled="!items.length" @export-pdf="savePDF" @export-csv="saveCSV" />
                <b-button variant="info" @click="loadItems" class="m-0">
                    <i class="fa fa-sync"></i>
                    Refresh
                </b-button>
                <b-button variant="info" @click="addSurplusReport" class="m-0">
                    <i class="fa fa-plus-circle"></i>
                    Report Surplus
                </b-button>
            </div>
        </template>

        <b-table striped :items="items" :fields="fields" :busy="isLoading" :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc" show-empty empty-text="No Surplus Reports." :selectable="true">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(created)="data">
                <b v-if="data.item.canceled || data.item.submitted" v-b-tooltip.hover :title="rowTitle(data.item)">
                    {{ data.item.created | formatDate('lll') }}
                </b>
                <span v-else>
                    {{ data.item.created | formatDate('lll') }}
                </span>
            </template>
            <template #cell(actions)="data">
                <div class="text-right">
                    <a href="#" @click.prevent="openDetails(data.item)">
                        <b-icon icon="pencil-square" v-b-tooltip.hover title="Details"></b-icon>
                    </a>
                    &nbsp;&nbsp;
                    <a href="#" @click.prevent="transferSurplus(data.item)">
                        <b-icon icon="arrow-left-right" v-b-tooltip.hover title="Transfer"></b-icon>
                    </a>
                </div>
            </template>
        </b-table>
        <!-- Surplus Report Form -->
        <b-modal id="surplus-report-form" size="xl" hide-footer :title="modalTitle">
            <SurplusReportFormComponent v-model="surplus" @refreshSurplusLists='loadItems' />
        </b-modal>
    </Card>
</template>

<script lang="ts">
import axios, { AxiosRequestConfig } from 'axios';
import { Component, Mixins } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { formatDateAndTime } from '@/util/DateUtils';
import SearchComponent from '@/components/SearchComponent.vue';
import SurplusReportFormComponent from "@/components/Surplus/SurplusReportFormComponent.vue";
import ExportDropdown from '@/components/ExportDropdown.vue';
import { ExportField, ReportExporter } from '@/mixins/ReportExporter';
import moment from 'moment';
import { Surplus } from '@/models/Surplus';

@Component({
    name: "SurplusReportsPage",
    components: {
        SearchComponent
        , SurplusReportFormComponent
        , ExportDropdown
    },
})

export default class InventoryBatchQueuePage extends Mixins(ReportExporter) {
    private isLoading = false;
    private items: Array<Surplus> = new Array<Surplus>();
    private surplus: Surplus = new Surplus();

    private fields: any[] = [
        { key: "created", label: "Created", sortable: true },
        { key: "createdBy", label: "Created By", sortable: true },
        { key: "manufacturer", label: "Manufacturer", sortable: true },
        { key: "drugName", label: "Drug Name", sortable: true },
        { key: "ndc", label: "NDC", formatter: this.ndcFormatter, sortable: true },
        { key: "quantity", label: "Quantity", sortable: true },
        { key: "unitCost", label: "Unit Cost", sortable: true },
        { key: "totalCost", label: "Total Cost", sortable: true },
        { key: "expirationDate", label: "Expiration Date", formatter: this.etaDateFormatter, sortable: true },
        { key: "actions", label: '' },
    ];

    private sortBy: string = 'created'
    private sortDesc: boolean = true;

    created() {
        this.loadItems();
    }

    loadItems() {
        this.$bvModal.hide('surplus-report-form');
        this.isLoading = true;
        const config: AxiosRequestConfig = { params: { } };
        axios.get<Array<Surplus>>('SurplusReport', config)
            .then(res => {
                this.items = res.data.map(i => new Surplus(i));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    ndcFormatter(value: string) {
        // Exports collapse a missing value to an empty string before the formatter runs, so a
        // `== null` guard falls through and renders "--" for a drug that simply has no NDC.
        if (!value) {
            return "";
        }
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    };

    etaDateFormatter(value: Date, key: string, item: any) {
        if (!value || item.estimatedArrivalDate > item.estimatedArrivalDate) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    get exportFields(): ExportField[] {
        // The table renders "created" through a slot, so the export supplies its own formatter.
        return this.ToExportFields(this.fields, {
            actions: null,
            created: { formatter: formatDateAndTime },
        });
    }

    savePDF() {
        this.ExportDataPDF("Surplus Reports", this.items, this.exportFields);
    }

    saveCSV() {
        this.ExportCSV("Surplus Reports", this.items, this.exportFields);
    }

    addSurplusReport() {
        this.surplus = new Surplus();
        this.surplus.id = 0;
        this.surplus.action = "New";
        this.$bvModal.show('surplus-report-form');
    }

    openDetails(surplus: Surplus) {
        this.surplus = structuredClone({ ...surplus, action: "Detail" });
        this.$bvModal.show('surplus-report-form');
    }

    transferSurplus(surplus: Surplus) {
        this.surplus = structuredClone({ ...surplus, action: "Transfer" });
        this.$bvModal.show('surplus-report-form');
    }

    get modalTitle() {
        if (!this.surplus.id) return "Add Surplus";
        if (this.surplus.action === "Transfer") return "Transfer Surplus";
        return "Surplus Details";
    }

}
</script>